// src/app/api/kitsApi.ts
//
// The yearly hardware package.
//   • Parents — /parent/kits      (delivery address, package status)
//   • Admin   — /admin/kits/*     (what goes in each age group's box,
//                                  and every shipment)

import { baseApi } from "./baseApi";

type Ok<T> = { success: true; data: T; message?: string };

export type KitTrack = "foundation_builders" | "explorers" | "innovators";

export type ShipmentStatus =
  | "awaiting_address"
  | "ready_to_pack"
  | "packed"
  | "shipped"
  | "delivered"
  | "cancelled";

export const SHIPMENT_STATUS_LABELS: Record<ShipmentStatus, string> = {
  awaiting_address: "Waiting for address",
  ready_to_pack: "Ready to pack",
  packed: "Packed",
  shipped: "On its way",
  delivered: "Delivered",
  cancelled: "Cancelled",
};

export const KIT_TRACK_LABELS: Record<KitTrack, string> = {
  foundation_builders: "Ages 6–9",
  explorers: "Ages 9–12",
  innovators: "Ages 13–18",
};

export interface DeliveryAddress {
  recipientName: string | null;
  phone: string | null;
  line1: string | null;
  line2: string | null;
  landmark: string | null;
  city: string | null;
  state: string | null;
}

export interface PackedItem {
  name: string;
  qty: number;
  unit: string | null;
}

export interface ParentShipment {
  id: string;
  childId: string;
  childName: string;
  track: KitTrack;
  kitTitle: string | null;
  academicYear: string;
  status: ShipmentStatus;
  items: PackedItem[];
  address: DeliveryAddress | null;
  courier: string | null;
  trackingRef: string | null;
  packedAt: string | null;
  shippedAt: string | null;
  deliveredAt: string | null;
  receivedByParent: boolean;
}

export interface KitItem {
  id?: string;
  name: string;
  qty: number;
  unit: string | null;
  usedIn: string | null;
  note: string | null;
  unitCost: number | null;
  ship: boolean;
}

export interface KitCatalogue {
  track: KitTrack;
  title: string | null;
  packingNotes: string | null;
  /** The package goes out once the child has paid for this many months of the year */
  shipAfterPaidMonths: number;
  items: KitItem[];
  shippedCount: number;
  estimatedCost: number | null;
  uncostedCount: number;
  updatedAt: string;
}

export interface AdminShipment {
  id: string;
  childId: string;
  childName: string;
  parentId: string;
  parentName: string | null;
  parentEmail: string | null;
  track: KitTrack;
  academicYear: string;
  deliveryMode: string | null;
  status: ShipmentStatus;
  items: PackedItem[];
  address: DeliveryAddress | null;
  addressComplete: boolean;
  courier: string | null;
  trackingRef: string | null;
  notes: string | null;
  packedAt: string | null;
  shippedAt: string | null;
  deliveredAt: string | null;
  receivedByParent: boolean;
  history: { status: string; at: string; byName: string | null }[];
  createdAt: string;
  nextStatuses: ShipmentStatus[];
}

export const kitsApi = baseApi
  .enhanceEndpoints({ addTagTypes: ["MyKits", "KitCatalogue", "KitShipments"] })
  .injectEndpoints({
    endpoints: (b) => ({
      // ── Parent ──────────────────────────────────────────────────
      getMyKits: b.query<
        Ok<{
          address: DeliveryAddress | null;
          addressComplete: boolean;
          suggestedRecipient: string | null;
          shipments: ParentShipment[];
        }>,
        void
      >({
        query: () => "/parent/kits",
        providesTags: ["MyKits"],
      }),

      saveKitAddress: b.mutation<Ok<{ address: DeliveryAddress; updatedShipments: number }>, DeliveryAddress>({
        query: (body) => ({ url: "/parent/kits/address", method: "PUT", body }),
        invalidatesTags: ["MyKits", "KitShipments"],
      }),

      confirmKitReceived: b.mutation<{ success: true; message: string }, string>({
        query: (id) => ({ url: `/parent/kits/${id}/received`, method: "POST" }),
        invalidatesTags: ["MyKits", "KitShipments"],
      }),

      // ── Admin ───────────────────────────────────────────────────
      getKitCatalogue: b.query<Ok<KitCatalogue[]>, void>({
        query: () => "/admin/kits/catalogue",
        providesTags: ["KitCatalogue"],
      }),

      updateKitCatalogue: b.mutation<
        Ok<KitCatalogue>,
        { track: KitTrack; body: { title?: string | null; packingNotes?: string | null; shipAfterPaidMonths?: number; items?: KitItem[] } }
      >({
        query: ({ track, body }) => ({ url: `/admin/kits/catalogue/${track}`, method: "PUT", body }),
        invalidatesTags: ["KitCatalogue"],
      }),

      getKitShipments: b.query<
        Ok<{
          shipments: AdminShipment[];
          total: number;
          page: number;
          pages: number;
          counts: Record<ShipmentStatus, number>;
          academicYears: string[];
        }>,
        { status?: string; track?: string; academicYear?: string; q?: string; page?: number }
      >({
        query: (p) => {
          const qs = new URLSearchParams();
          Object.entries(p).forEach(([k, v]) => v !== undefined && v !== "" && qs.set(k, String(v)));
          return `/admin/kits/shipments?${qs.toString()}`;
        },
        providesTags: ["KitShipments"],
      }),

      updateKitShipment: b.mutation<
        Ok<AdminShipment>,
        {
          id: string;
          body: Partial<{
            status: ShipmentStatus;
            courier: string | null;
            trackingRef: string | null;
            notes: string | null;
            address: DeliveryAddress;
          }>;
        }
      >({
        query: ({ id, body }) => ({ url: `/admin/kits/shipments/${id}`, method: "PATCH", body }),
        invalidatesTags: ["KitShipments"],
      }),

      syncKitShipments: b.mutation<Ok<{ checked: number; created: number }>, { academicYear?: string }>({
        query: (body) => ({ url: "/admin/kits/shipments/sync", method: "POST", body }),
        invalidatesTags: ["KitShipments"],
      }),
    }),
  });

export const {
  useGetMyKitsQuery,
  useSaveKitAddressMutation,
  useConfirmKitReceivedMutation,
  useGetKitCatalogueQuery,
  useUpdateKitCatalogueMutation,
  useGetKitShipmentsQuery,
  useUpdateKitShipmentMutation,
  useSyncKitShipmentsMutation,
} = kitsApi;
